import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import Papa from 'papaparse';
import { validate as isValidUUID, v4 as uuidv4 } from 'uuid';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';

import { useBatchedBulkDeleteVoucherUsers } from '~/hooks/apis/promo/useBulkDeleteVoucherUsers';
import { allVoucherUsersQueryKeys } from '~/hooks/apis/promo/useListAllVoucherUsers';

import { BulkDeleteVoucherUsersResponse } from '~/services/PromoService';

const BATCH_SIZE = 200;
const CSV_HEADERS = ['user_id', 'userId', 'customer_id', 'customerId'];

type Props = {
  open: boolean;
  onClose: () => void;
  voucherId: string;
  voucherName: string;
};

type DeleteSummary = {
  requestedCount: number;
  skippedCount: number;
  deletedCount: number;
  voucherName: string;
};

type UploadedUserRow = {
  id: string;
  position: number;
  userId: string;
};

const UPLOADED_USER_COLUMNS: Array<GridColDef<UploadedUserRow>> = [
  { field: 'position', headerName: '#', width: 90 },
  { field: 'userId', headerName: 'User ID', flex: 1, minWidth: 260 },
];

function formatCount(count: number): string {
  return count.toLocaleString();
}

class BatchRunError extends Error {
  constructor(
    readonly completed: DeleteSummary,
    readonly completedBatchCount: number,
    readonly batchCount: number,
    cause: unknown,
  ) {
    super(cause instanceof Error ? cause.message : 'Unknown request error');
    this.name = 'BatchRunError';
  }
}

export default function BulkDeleteVoucherUsersDialog({ open, onClose, voucherId, voucherName }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const bulkDeleteMutation = useBatchedBulkDeleteVoucherUsers();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState('');
  const [userIds, setUserIds] = useState<Array<string>>([]);
  const [invalidCount, setInvalidCount] = useState(0);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState<DeleteSummary | null>(null);
  const [partialDeletion, setPartialDeletion] = useState<DeleteSummary | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [isDeleting, setIsDeleting] = useState(false);

  const uploadedUserRows = useMemo(
    () => userIds.map((userId, index) => ({ id: userId, position: index + 1, userId })),
    [userIds],
  );

  const reset = useCallback(() => {
    setFileName('');
    setUserIds([]);
    setInvalidCount(0);
    setDuplicateCount(0);
    setProgress(null);
    setError('');
    setCompleted(null);
    setPartialDeletion(null);
    setPaginationModel({ page: 0, pageSize: 25 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleClose = useCallback(() => {
    if (!isDeleting) {
      reset();
      onClose();
    }
  }, [isDeleting, onClose, reset]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setFileName(file.name);
    setCompleted(null);
    setPartialDeletion(null);
    setError('');
    setPaginationModel((current) => ({ ...current, page: 0 }));
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (headerResult) => {
        const header = headerResult.meta.fields?.find((field) => CSV_HEADERS.includes(field));
        const setParsedUserIds = (rawUserIds: Array<string>) => {
          const trimmedUserIds = rawUserIds.map((value) => String(value).trim()).filter(Boolean);
          const uniqueUserIds = Array.from(new Set(trimmedUserIds));
          const validUserIds = uniqueUserIds.filter(isValidUUID);
          setUserIds(validUserIds);
          setDuplicateCount(trimmedUserIds.length - uniqueUserIds.length);
          setInvalidCount(uniqueUserIds.length - validUserIds.length);
        };
        if (header) {
          setParsedUserIds(headerResult.data.map((row) => row[header] ?? ''));
          return;
        }
        Papa.parse<Array<string>>(file, {
          skipEmptyLines: true,
          complete: (noHeaderResult) => {
            const rawUserIds = noHeaderResult.data.map((row) => row[0] ?? '');
            setParsedUserIds(
              rawUserIds.length > 0 && !isValidUUID(String(rawUserIds[0]).trim()) ? rawUserIds.slice(1) : rawUserIds,
            );
          },
          error: (parseError) => setError(`Failed to parse CSV: ${parseError.message}`),
        });
      },
      error: (parseError) => setError(`Failed to parse CSV: ${parseError.message}`),
    });
  }, []);

  const invalidateVoucherUserQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['voucher-users', voucherId] }),
      queryClient.invalidateQueries({ queryKey: allVoucherUsersQueryKeys.all }),
      queryClient.invalidateQueries({ queryKey: ['voucher', voucherId] }),
    ]);
  }, [queryClient, voucherId]);

  const handleDelete = useCallback(async () => {
    if (userIds.length === 0) {
      return;
    }

    const batches = Array.from({ length: Math.ceil(userIds.length / BATCH_SIZE) }, (_, index) =>
      userIds.slice(index * BATCH_SIZE, (index + 1) * BATCH_SIZE),
    );
    const batchId = uuidv4();
    let total: DeleteSummary = { requestedCount: 0, skippedCount: 0, deletedCount: 0, voucherName };

    setError('');
    setCompleted(null);
    setPartialDeletion(null);
    setProgress(0);
    setIsDeleting(true);
    try {
      for (let index = 0; index < batches.length; index += 1) {
        let response: BulkDeleteVoucherUsersResponse;
        try {
          response = await bulkDeleteMutation.mutateAsync({
            voucher_id: voucherId,
            voucher_users: batches[index].map((user_id) => ({ user_id })),
            dryRun: false,
            batchId,
            batchRunCountTotal: batches.length,
            batchRunCount: index + 1,
          });
        } catch (requestError) {
          throw new BatchRunError(total, index, batches.length, requestError);
        }

        total = {
          requestedCount: total.requestedCount + batches[index].length,
          skippedCount: total.skippedCount + response.result.skipped.length,
          deletedCount: total.deletedCount + response.result.deletedVoucherUsers.length,
          voucherName: response.result.voucherName,
        };
        setProgress(Math.round(((index + 1) / batches.length) * 100));
      }
      setCompleted(total);
      enqueueSnackbar(`Deleted ${formatCount(total.deletedCount)} voucher user${total.deletedCount === 1 ? '' : 's'}`, {
        variant: 'success',
      });
      await invalidateVoucherUserQueries();
    } catch (requestError) {
      if (requestError instanceof BatchRunError) {
        setPartialDeletion(requestError.completed);
        setError(
          `Deletion stopped after ${formatCount(requestError.completedBatchCount)} of ${formatCount(requestError.batchCount)} batches. ${formatCount(requestError.completed.deletedCount)} voucher user${requestError.completed.deletedCount === 1 ? '' : 's'} were deleted before the failure. ${requestError.message}`,
        );
        await invalidateVoucherUserQueries();
      } else {
        setError(`Deletion failed: ${(requestError as Error).message}`);
      }
    } finally {
      setProgress(null);
      setIsDeleting(false);
    }
  }, [bulkDeleteMutation, enqueueSnackbar, invalidateVoucherUserQueries, userIds, voucherId, voucherName]);

  const summary = completed ?? partialDeletion;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk delete voucher users from CSV</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!completed && !partialDeletion && (
            <>
              <Typography variant="body2" color="text.secondary">
                Select a CSV to build the deletion list locally. Review the paginated list before deleting; only bounded
                200-user requests are sent to the service.
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Expected CSV: a <code>user_id</code>, <code>userId</code>, <code>customer_id</code>, or{' '}
                  <code>customerId</code> column; headerless single-column files are also accepted.
                </Typography>
                <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={isDeleting}>
                  Choose CSV
                  <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={handleFileChange} />
                </Button>
                {fileName && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {fileName}: {formatCount(userIds.length)} valid ID{userIds.length === 1 ? '' : 's'}
                    {duplicateCount > 0 ? `, ${formatCount(duplicateCount)} duplicate row(s) ignored` : ''}
                    {invalidCount > 0 ? `, ${formatCount(invalidCount)} invalid ID(s) ignored` : ''}.
                  </Typography>
                )}
              </Paper>
            </>
          )}

          {summary && (
            <Alert severity={completed ? 'success' : 'warning'}>
              {completed
                ? `Deleted ${formatCount(summary.deletedCount)} of ${formatCount(summary.requestedCount)} voucher users from “${summary.voucherName}”. ${formatCount(summary.skippedCount)} were skipped.`
                : `Deleted ${formatCount(summary.deletedCount)} of ${formatCount(summary.requestedCount)} submitted voucher users from “${summary.voucherName}” before the operation stopped. ${formatCount(summary.skippedCount)} were skipped.`}
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {progress !== null && <LinearProgress variant="determinate" value={progress} />}
          {uploadedUserRows.length > 0 && !completed && !partialDeletion && (
            <Paper variant="outlined" sx={{ height: 440, p: 1 }}>
              <Typography variant="subtitle2" sx={{ px: 1, pt: 0.5 }}>
                Users to delete ({formatCount(uploadedUserRows.length)})
              </Typography>
              <DataGrid
                rows={uploadedUserRows}
                columns={UPLOADED_USER_COLUMNS}
                pagination
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnMenu
                sx={{ border: 0, mt: 1 }}
              />
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleClose} disabled={isDeleting}>
          {completed ? 'Close' : 'Cancel'}
        </Button>
        {!completed && !partialDeletion && (
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isDeleting || userIds.length === 0}
          >
            Delete {formatCount(userIds.length)} voucher user{userIds.length === 1 ? '' : 's'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
